/**
 * /sudo/templates/:key/:channel — edit one template.
 *
 * Textarea + variable chip toolbar + live preview + SMS char budget
 * indicator. Save updates the global (tenant_id IS NULL) row.
 */
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  Textarea,
} from '@bookaburra/ui';
import { useDocumentTitle } from '../lib/use-document-title';
import { TemplateEditor } from '../components/TemplateEditor';

interface TemplateDetail {
  key: string;
  channel: 'email_subject' | 'email_body' | 'sms';
  locale: string;
  body: string;
  updatedAt: number;
  updatedBy: string | null;
  variables: Array<{ name: string; worstCaseLength: number; description: string }>;
}

async function fetchTemplate(
  key: string,
  channel: string,
): Promise<TemplateDetail> {
  const r = await fetch(
    `/api/super/templates/${encodeURIComponent(key)}/${encodeURIComponent(channel)}`,
  );
  if (!r.ok) throw new Error(`load_failed_${r.status}`);
  return r.json();
}

async function saveTemplate(
  key: string,
  channel: string,
  body: string,
): Promise<void> {
  const r = await fetch(
    `/api/super/templates/${encodeURIComponent(key)}/${encodeURIComponent(channel)}`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ body }),
    },
  );
  if (!r.ok) {
    const j = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(j.error ?? `error ${r.status}`);
  }
}

export const Route = createFileRoute('/sudo/templates/$key/$channel')({
  component: TemplateEditorPage,
});

function TemplateEditorPage() {
  const { key, channel } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  useDocumentTitle(`Edit ${key} / ${channel} — Super-admin`);

  const detailQ = useQuery({
    queryKey: ['sudo', 'templates', key, channel],
    queryFn: () => fetchTemplate(key, channel),
  });

  const [body, setBody] = useState<string>('');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  useEffect(() => {
    if (detailQ.data) setBody(detailQ.data.body);
  }, [detailQ.data]);

  const saveMut = useMutation({
    mutationFn: () => saveTemplate(key, channel, body),
    onSuccess: () => {
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['sudo', 'templates'] });
    },
  });

  const dirty = detailQ.data && body !== detailQ.data.body;

  return (
    <div>
      <Link
        to="/sudo/templates"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> All templates
      </Link>
      <header className="mt-2 mb-6">
        <h1 className="text-2xl font-bold tracking-tight">
          {key} <span className="text-muted-foreground">/</span> {channel}
        </h1>
        <p className="mt-1 text-xs text-muted-foreground">
          English (Australia) — the global default every business sees unless
          they've customised it on the Large business plan.
        </p>
      </header>

      {detailQ.isLoading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-secondary" />
      ) : detailQ.error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t load template</AlertTitle>
          <AlertDescription>{(detailQ.error as Error).message}</AlertDescription>
        </Alert>
      ) : detailQ.data ? (
        <Card className="p-5">
          <TemplateEditor
            templateKey={key}
            channel={channel as TemplateDetail['channel']}
            value={body}
            onChange={setBody}
            variables={detailQ.data.variables}
          />
          {saveMut.error ? (
            <Alert variant="destructive" className="mt-4">
              <AlertTitle>Couldn&rsquo;t save</AlertTitle>
              <AlertDescription>{(saveMut.error as Error).message}</AlertDescription>
            </Alert>
          ) : null}
          {savedAt && !dirty ? (
            <Alert variant="success" className="mt-4">
              <AlertTitle>Saved</AlertTitle>
              <AlertDescription>
                The new template body is live. Future notifications will use
                it.
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="mt-5 flex items-center justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate({ to: '/sudo/templates' })}
            >
              Cancel
            </Button>
            <Button
              onClick={() => saveMut.mutate()}
              loading={saveMut.isPending}
              disabled={!dirty || saveMut.isPending}
            >
              <Save className="h-4 w-4" /> Save
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
